import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Field, FieldProps, Formik, FormikProps } from "formik";
import { Button, Form } from "react-bootstrap";
import ReactSelect from "../../../../components/inputs/select";
import { SelectOption } from "../../../../types";
import { PlusLg, X } from "react-bootstrap-icons";
import { StyledInput } from "../../../../components/inputs/styledInput";
import { LocationModal } from "../../components/locationModal/LocationModal";
import { useTranslation } from "react-i18next";
import { LocationRequest } from "../../types";
import { useCountriesQuery } from "../../../../graphql/country.hooks";
import { PageQueryHookResult } from "../../../../graphql/page.hooks";

type Props = {
  isActive: boolean;
  locationRestriction: PageQueryHookResult["data"]["page"]["pageLocationRestrictions"];
};

export type LocationItem =
  PageQueryHookResult["data"]["page"]["pageLocationRestrictions"]["pageLocationRestrictionItems"][number];

export type LocationAccessFormHandle = {
  getFormValues: () => LocationRequest;
  submitForm: () => void;
};

export const LocationAccessForm = forwardRef<LocationAccessFormHandle, Props>(
  ({ locationRestriction, isActive }, ref) => {
    const { t } = useTranslation();

    const { data: contriesData } = useCountriesQuery();

    const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const formikRef = React.useRef<FormikProps<LocationRequest>>(null);

    const openLocationModal = () => setShowLocationModal(true);
    const closeLocationModal = () => setShowLocationModal(false);

    const formatedLocationItems =
      locationRestriction?.pageLocationRestrictionItems?.map(
        (location: LocationItem) => {
          return {
            countryId: location?.country?.id,
            cityId: location?.city?.id,
            longitude: location?.longitude,
            latitude: location?.latitude,
            radius: location?.radius,
            rule: location?.rule,
          };
        }
      );

    const initialValues: LocationRequest = {
      isExactRequired: (locationRestriction?.isExactRequired ? 1 : 0) || 1,
      isExactCheck: (locationRestriction?.isExactCheck ? 1 : 0) || 0,
      rule: locationRestriction
        ? !!locationRestriction?.pageLocationRestrictionItems?.[0]?.rule
        : false,
      locationHint: locationRestriction?.hint || t("page.defaultLocationError"),
      locationItems: formatedLocationItems || [],
    };

    useImperativeHandle(ref, () => ({
      getFormValues: () => {
        const currentValues = formikRef.current?.values || initialValues;
        return { ...currentValues, isExactRequired: isActive ? 1 : 0 };
      },
      submitForm: () => {
        formikRef.current?.handleSubmit();
      },
    }));

    const getCountryTitle = (countryId: number) => {
      return contriesData?.countries?.find(({ id }) => id === countryId)?.title;
    };

    const ruleOptions = [
      {
        label: (
          <span
            dangerouslySetInnerHTML={{ __html: t("page.contentAvailable") }}
          />
        ),
        value: true,
      },
      {
        label: (
          <span
            dangerouslySetInnerHTML={{ __html: t("page.contentNotAvailable") }}
          />
        ),
        value: false,
      },
    ];

    if (!isActive) {
      return <></>;
    }

    return (
      <div className="mb-4">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={() => {}}
          enableReinitialize
        >
          {({ touched, errors, setFieldValue, values }) => {
            return (
              <Form>
                <Form.Group className="d-flex align-items-center mb-3">
                  <Field name="isExactCheck">
                    {({ field }: FieldProps<0 | 1>) => (
                      <Form.Check
                        type="switch"
                        id="isExactCheck"
                        {...field}
                        checked={!!field.value}
                        onChange={(e) =>
                          setFieldValue(
                            "isExactCheck",
                            e.target.checked ? 1 : 0
                          )
                        }
                      />
                    )}
                  </Field>
                  <Form.Label
                    htmlFor="isExactCheck"
                    className="mb-0 fs-7 text-dark-gray"
                  >
                    {t("page.Ask Location")}
                  </Form.Label>
                </Form.Group>
                <Field name="rule">
                  {() => (
                    <ReactSelect
                      className="mb-3 text-dark-gray"
                      options={ruleOptions}
                      value={ruleOptions.find(
                        (rule) => rule.value === values.rule
                      )}
                      onChange={(option: SelectOption) => {
                        setFieldValue("rule", option.value);
                      }}
                      label={t("page.Restriction Rule")}
                    />
                  )}
                </Field>
                <Button
                  variant="white"
                  className="d-flex align-items-center p-0 mb-4"
                  onClick={() => {
                    setSelectedLocation(null);
                    openLocationModal();
                  }}
                >
                  <PlusLg className="fs-6 me-2 text-primary" />
                  <p className="fs-6 text-primary fw-normal m-0">
                    {t("page.Add Location")}
                  </p>
                </Button>
                <div
                  className={`d-flex flex-wrap gap-3 ${values.locationItems.length && "mb-4"}`}
                >
                  {values.locationItems
                    .filter(({ rule }) => !!rule === values.rule)
                    .map((item, index) => (
                      <div
                        key={index}
                        className="location-item bg-primary-light rounded-1 d-flex align-items-center cursor-pointer"
                        onClick={() => {
                          setSelectedLocation({ ...item, index });
                          openLocationModal();
                        }}
                      >
                        <p className="text-primary fw-bold fs-6 mb-0 me-2">
                          {item.countryId
                            ? getCountryTitle(item.countryId)
                            : String(item.latitude).slice(0, 7) + "..."}
                        </p>
                        <X
                          className="fs-3 fw-bold text-primary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedLocationItems =
                              values.locationItems.filter(
                                (_, idx) => idx !== index
                              );
                            setFieldValue(
                              "locationItems",
                              updatedLocationItems
                            );
                          }}
                        />
                      </div>
                    ))}
                </div>
                <div className="form-floating">
                  <StyledInput
                    name="locationHint"
                    disabled={!values.locationItems.length}
                    className="text-dark-gray"
                    label={t("page.Location Restriction Error Message")}
                    error={!!(touched.locationHint && errors.locationHint)}
                  />
                </div>
                <LocationModal
                  show={showLocationModal}
                  location={selectedLocation}
                  onConfirm={(
                    location: LocationItem,
                    editingIndex: number | null
                  ) => {
                    if (editingIndex !== null) {
                      setFieldValue(
                        "locationItems",
                        values.locationItems.map((item, index) =>
                          index === editingIndex
                            ? { ...location, rule: values.rule ? 1 : 0 }
                            : item
                        )
                      );
                    } else {
                      setFieldValue("locationItems", [
                        ...values.locationItems,
                        { ...location, rule: values.rule ? 1 : 0 },
                      ]);
                    }

                    closeLocationModal();
                  }}
                  onCancel={() => {
                    setSelectedLocation(null);
                    closeLocationModal();
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }
);
LocationAccessForm.displayName = "LocationAccessForm";
